export const CAMERA_FAILURE = {
  DENIED: "denied",
  NO_DEVICE: "no_device",
  DEVICE_UNAVAILABLE: "device_unavailable",
  CONSTRAINTS: "constraints",
  INSECURE_CONTEXT: "insecure_context",
  ABORTED: "aborted",
  UNKNOWN: "unknown"
}

// Failures where the fix lives in the user's environment, not our code
export const ENVIRONMENT_FAILURES = [
  CAMERA_FAILURE.DENIED,
  CAMERA_FAILURE.NO_DEVICE,
  CAMERA_FAILURE.DEVICE_UNAVAILABLE,
  CAMERA_FAILURE.INSECURE_CONTEXT
]

// Narrower on purpose: denied and no_device are ordinary traffic, metrics-only, not alerts
export const SENTRY_ALERT_FAILURES = [
  CAMERA_FAILURE.DEVICE_UNAVAILABLE,
  CAMERA_FAILURE.INSECURE_CONTEXT
]

// A stored block or Permissions-Policy denial rejects near-instantly; a live human
// decision on a prompt cannot happen this fast
export const FAST_REJECTION_MS = 100

// Which help a blocked user needs. Null means the default permission-reset flow;
// the pages own the header and body each key maps to.
export const CAMERA_DIAGNOSIS = {
  IN_APP: "inApp",
  NO_DEVICE: "noDevice",
  DEVICE_UNAVAILABLE: "deviceUnavailable",
  CAMERA_IMPOSSIBLE: "cameraImpossible",
  FRAMED_DENIAL: "framedDenial"
}

export const resolveCameraDiagnosis = ({deviceInfo = {}, cameraFailure} = {}) => {
  const failureType = cameraFailure?.failureType
  // In-app first: webviews often also fail the capability check, and this advice is actionable
  if (deviceInfo.isInAppBrowser) return CAMERA_DIAGNOSIS.IN_APP
  if (failureType === CAMERA_FAILURE.NO_DEVICE) return CAMERA_DIAGNOSIS.NO_DEVICE
  if (failureType === CAMERA_FAILURE.DEVICE_UNAVAILABLE) return CAMERA_DIAGNOSIS.DEVICE_UNAVAILABLE

  const capabilityBlocked = deviceInfo.features
    ? !deviceInfo.features.secureContext || !deviceInfo.features.mediaDevices
    : false
  // The capability probe can read false spuriously, so it never overrides an explicit denial
  if (failureType === CAMERA_FAILURE.INSECURE_CONTEXT || (capabilityBlocked && failureType !== CAMERA_FAILURE.DENIED)) {
    return CAMERA_DIAGNOSIS.CAMERA_IMPOSSIBLE
  }
  // A proven live tap (fastRejection false) keeps the permission flow; unknown leans
  // framed, because the observer can be uninstallable in exactly these hardened embeds
  if (failureType === CAMERA_FAILURE.DENIED && deviceInfo.isFramed && cameraFailure?.fastRejection !== false) {
    return CAMERA_DIAGNOSIS.FRAMED_DENIAL
  }
  return null
}

// DOMException names carry stable WebRTC-spec semantics; the second name in each pair is the legacy Chrome alias
const FAILURE_BY_NAME = {
  NotAllowedError: CAMERA_FAILURE.DENIED,
  PermissionDeniedError: CAMERA_FAILURE.DENIED,
  NotFoundError: CAMERA_FAILURE.NO_DEVICE,
  DevicesNotFoundError: CAMERA_FAILURE.NO_DEVICE,
  NotReadableError: CAMERA_FAILURE.DEVICE_UNAVAILABLE,
  TrackStartError: CAMERA_FAILURE.DEVICE_UNAVAILABLE,
  OverconstrainedError: CAMERA_FAILURE.CONSTRAINTS,
  ConstraintNotSatisfiedError: CAMERA_FAILURE.CONSTRAINTS,
  SecurityError: CAMERA_FAILURE.INSECURE_CONTEXT,
  AbortError: CAMERA_FAILURE.ABORTED
}

// Fallback when only the message survives; anchored to camera phrasing so an unrelated SDK error can't match
const FAILURE_BY_MESSAGE = [
  [CAMERA_FAILURE.DENIED, /permission (denied|dismissed)|(camera|video|getusermedia).{0,30}permission|not allowed by the user agent/i],
  [CAMERA_FAILURE.NO_DEVICE, /requested device not found|no camera/i],
  [CAMERA_FAILURE.DEVICE_UNAVAILABLE, /could not start video|not ?readable|(camera|video).{0,30}(in use|unavailable|hardware|failed|failure)/i],
  [CAMERA_FAILURE.CONSTRAINTS, /constrain/i],
  [CAMERA_FAILURE.INSECURE_CONTEXT, /secure context/i],
  [CAMERA_FAILURE.ABORTED, /abort/i]
]

export const classifyCameraError = (error) => {
  if (!error) return CAMERA_FAILURE.UNKNOWN
  if (FAILURE_BY_NAME[error.name]) return FAILURE_BY_NAME[error.name]
  const message = error.message || ""
  return FAILURE_BY_MESSAGE.find(([, pattern]) => pattern.test(message))?.[0] ?? CAMERA_FAILURE.UNKNOWN
}

let lastRejection = null
let observing = false
let unpatch = null

/** Test seam: classifyCameraEvent does its own consume-once read inline. */
export const consumeGetUserMediaRejection = () => {
  const rejection = lastRejection
  lastRejection = null
  return rejection
}

/**
 * Classifies an SDK error event, pairing it with an observed getUserMedia rejection
 * only when the event cannot classify itself or the two agree: an unrelated error
 * must not inherit a rejection's identity. A rejection is spent by the first event it
 * is weighed against, so a disagreement cannot leave it behind for a later event.
 */
export const classifyCameraEvent = (detail, windowMs = 3000) => {
  const detailType = classifyCameraError(detail)
  const fresh = lastRejection && Date.now() - lastRejection.at < windowMs ? lastRejection : null
  const observedType = fresh ? classifyCameraError(fresh) : null
  const observed = fresh && (detailType === CAMERA_FAILURE.UNKNOWN || detailType === observedType)
    ? fresh
    : null
  if (fresh) lastRejection = null

  return {
    failureType: observed ? observedType : detailType,
    errorName: observed?.name ?? detail?.name ?? null,
    message: detail?.message ?? observed?.message ?? null,
    elapsedMs: observed?.elapsedMs ?? null,
    fastRejection: observed ? observed.elapsedMs <= FAST_REJECTION_MS : null
  }
}

/** Test seam. Restores the original, so a reinstall cannot stack a second wrapper. */
export const resetGetUserMediaObserver = () => {
  lastRejection = null
  observing = false
  unpatch?.()
  unpatch = null
}

/**
 * Pass-through observer on getUserMedia: the SDK owns the call and may strip the
 * DOMException name, so observing the source keeps the true name and timing.
 */
export const observeGetUserMedia = (mediaDevices = navigator.mediaDevices) => {
  if (observing || !mediaDevices?.getUserMedia) return
  observing = true

  // Hardened environments freeze mediaDevices; diagnostics must never break what they observe
  try {
    const original = mediaDevices.getUserMedia
    mediaDevices.getUserMedia = (...args) => {
      const startedAt = performance.now()
      return original.call(mediaDevices, ...args).then(
        stream => {
          lastRejection = null
          return stream
        },
        error => {
          lastRejection = {
            name: error?.name ?? null,
            message: error?.message ?? null,
            elapsedMs: Math.round(performance.now() - startedAt),
            at: Date.now()
          }
          throw error
        }
      )
    }
    unpatch = () => {
      mediaDevices.getUserMedia = original
    }
  } catch {
  }
}
